import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaShoppingCart, FaBell, FaStar } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../auth/AuthContext';
import { useVehicle } from '../contexts/VehicleContext';
import { listingService, searchService, accountSettingsService } from '../services/apiService';
import { ProductDetailSkeleton } from './Skeleton';
import PhotoGallery from './partDetail/PhotoGallery';
import FitmentStrip from './partDetail/FitmentStrip';
import PriceBreakdownCard from './partDetail/PriceBreakdownCard';
import SellerBlock from './partDetail/SellerBlock';
import MoreSellersSection from './partDetail/MoreSellersSection';
import QASection from './partDetail/QASection';
import CrossSellSection from './partDetail/CrossSellSection';
import { useCrossSell } from '../hooks/useCrossSell';
import { buildMarketplaceCheckoutPath } from '../utils/partDetailRoutes';
import { useThemeClasses } from '../hooks/useThemeClasses';

/**
 * B-04 — Part Detail (New OEM / Aftermarket)
 */
export default function PartDetailNew({ listingIdOverride } = {}) {
  const { itemId } = useParams();
  const listingId = listingIdOverride || itemId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refreshCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { selectedVehicle } = useVehicle();
  const { isDark, sc } = useThemeClasses();

  const vehicleIdParam = searchParams.get('vehicleId');
  const columnParam = searchParams.get('column') || 'oem';
  const nodeIdParam = searchParams.get('nodeId');
  const catalogNodeIdParam = searchParams.get('catalogNodeId');

  const resolvedVehicleId =
    vehicleIdParam ||
    selectedVehicle?._id ||
    selectedVehicle?.id ||
    null;

  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [outOfStock, setOutOfStock] = useState(false);
  const [moreSellers, setMoreSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [qaThreads, setQaThreads] = useState([]);
  const [adding, setAdding] = useState(false);

  const nodeId = catalogNodeIdParam || nodeIdParam || listing?.catalogNodeId || listing?.nodeId;
  const crossSell = useCrossSell(nodeId, listingId, 12);

  const loadListing = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    setOutOfStock(false);
    try {
      const data = await listingService.getListing(listingId, {
        vehicleId: resolvedVehicleId,
      });
      if (data?.column === 'used' || data?.listingType === 'used') {
        const p = new URLSearchParams(searchParams);
        p.set('column', 'used');
        navigate(`/catalog/used-p-${listingId}?${p.toString()}`, { replace: true });
        return;
      }
      setListing(data);
    } catch (err) {
      if (err.status === 404) {
        setError('unavailable');
      } else {
        setError(err.message || 'Failed to load listing');
      }
    } finally {
      setLoading(false);
    }
  }, [listingId, resolvedVehicleId, searchParams, navigate]);

  useEffect(() => {
    if (columnParam === 'used' && listingId) {
      const p = new URLSearchParams(searchParams);
      navigate(`/catalog/used-p-${listingId}?${p.toString()}`, { replace: true });
      return;
    }
    loadListing();
  }, [loadListing, columnParam, listingId, navigate, searchParams]);

  const handleNotifyMe = async () => {
    if (!isAuthenticated) {
      toast.info('Please log in to get stock alerts');
      navigate('/login', { state: { from: location } });
      return;
    }
    try {
      await accountSettingsService.addToWishlist({
        partName: listing?.title || listing?.name || 'Part',
        listingId: listing?.listingId || listingId,
        productId: listing?.productId,
        nodeId: nodeId || listing?.nodeId,
        catalogNodeId: catalogNodeIdParam || listing?.catalogNodeId || (nodeId?.length === 64 ? nodeId : undefined),
        catalogColumn: columnParam,
        vehicleId: resolvedVehicleId,
        partNumber: listing?.partNumber,
        price: listing?.pricing?.payable ?? listing?.pricing?.price,
        source: 'new',
        notifyWhenAvailable: true,
      });
      toast.success('Saved — we will notify you when back in stock');
    } catch (err) {
      toast.error(err.message || 'Could not save alert');
    }
  };

  useEffect(() => {
    if (!nodeId) return;
    let cancelled = false;

    (async () => {
      setSellersLoading(true);
      try {
        const res = await searchService.getListings(nodeId, {
          column: columnParam,
          vehicleId: resolvedVehicleId,
        });
        if (!cancelled) setMoreSellers(res.listings || []);
      } catch {
        if (!cancelled) setMoreSellers([]);
      } finally {
        if (!cancelled) setSellersLoading(false);
      }
    })();

    (async () => {
      try {
        const res = await listingService.getQa(nodeId);
        if (!cancelled) setQaThreads(res.threads || []);
      } catch {
        if (!cancelled) setQaThreads([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nodeId, columnParam, resolvedVehicleId]);

  const handleAddToCart = async () => {
    if (!listing) return;
    if (!isAuthenticated) {
      navigate('/login', {
        state: { from: location.pathname + location.search, action: 'addToCart' },
      });
      return;
    }
    setAdding(true);
    try {
      await listingService.addToCart({
        listingId: listing.listingId,
        quantity: 1,
        vehicleId: resolvedVehicleId,
      });
      await refreshCart();
      toast.success(`${listing.name} added to cart!`);
    } catch (err) {
      if (err.status === 401) {
        navigate('/login', { state: { from: location.pathname + location.search } });
        return;
      }
      if (err.status === 409) {
        setOutOfStock(true);
        toast.error('This item just went out of stock.');
        return;
      }
      if (err.status === 404) {
        setError('unavailable');
        toast.error('This listing is no longer available.');
        return;
      }
      toast.error(err.message || 'Failed to add to cart.');
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = () => {
    if (!listing) return;

    const marketplaceCatalogNodeId =
      catalogNodeIdParam || listing?.catalogNodeId || nodeId;
    if (marketplaceCatalogNodeId && listing.listingId) {
      const checkoutPath = buildMarketplaceCheckoutPath({
        vehicleId: resolvedVehicleId,
        catalogNodeId: marketplaceCatalogNodeId,
        listingId: listing.listingId,
      });
      if (!isAuthenticated) {
        navigate('/login', { state: { from: checkoutPath } });
        return;
      }
      navigate(checkoutPath);
      return;
    }

    const buyNowItem = {
      id: listing.listingId,
      productId: listing.listingId,
      name: listing.name,
      brand: listing.brand,
      price: listing.pricing?.mrp || listing.pricing?.partPrice,
      discountPrice: listing.pricing?.partPrice,
      imageUrl: listing.photos?.[0],
      partNumber: listing.partNumber,
      seller: listing.seller?.city,
      quantity: 1,
      listingId: listing.listingId,
      vehicleId: resolvedVehicleId,
      pricing: listing.pricing,
    };

    localStorage.removeItem('buyNowItem');
    if (!isAuthenticated) {
      localStorage.setItem('pendingBuyNowProduct', JSON.stringify(buyNowItem));
      navigate('/login', {
        state: { from: location.pathname, action: 'buyNow', returnTo: '/checkout/address' },
      });
      return;
    }
    localStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));
    navigate('/checkout/address');
  };

  const handleAskQuestion = async (question) => {
    if (!nodeId) throw new Error('Catalog node unavailable');
    const res = await listingService.postQa(nodeId, question);
    setQaThreads((prev) => [res.thread, ...prev]);
  };

  const handleLoginForQa = () => {
    navigate('/login', { state: { from: location.pathname + location.search } });
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (error === 'unavailable') {
    return (
      <div className={`${sc('pdpShell')} px-4 py-16 text-center`}>
        <div className="mx-auto max-w-md rounded-xl border border-slate-700/60 bg-slate-800/50 p-8">
          <p className="text-lg font-semibold text-slate-200">
            This listing is no longer available.
          </p>
          <p className="mt-2 text-sm text-slate-400">
            It may have been removed or sold out. Try searching for alternatives.
          </p>
          <button
            type="button"
            onClick={() => navigate('/catalog')}
            className="mt-6 rounded-xl bg-blue-600 px-6 py-2.5 font-bold text-white hover:bg-blue-500"
          >
            Browse alternatives
          </button>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className={`${sc('pdpShell')} flex items-center justify-center px-4`}>
        <p className="text-slate-400">{error || 'Product not found'}</p>
      </div>
    );
  }

  const showOos = outOfStock || listing.inStock === false;
  const sellerRating = Number(listing.seller?.rating || 0);
  const hasDiscount =
    listing.pricing?.mrp && listing.pricing.mrp > listing.pricing.partPrice;
  const backHref = catalogNodeIdParam
    ? `/catalog/node/${catalogNodeIdParam}${resolvedVehicleId ? `?vehicleId=${resolvedVehicleId}` : ''}`
    : null;

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: listing.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      }
    } catch {
      /* dismissed */
    }
  };

  const actionButtons = (className = '', mobileCompact = false) => (
    <div className={`flex w-full gap-2.5 sm:gap-3 ${className}`}>
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={adding}
        className={`${sc('pdpAddToCartBtn')} ${mobileCompact ? 'min-w-0 px-3 py-3 text-xs sm:text-sm' : ''}`}
      >
        <FaShoppingCart className="shrink-0 text-sm" />
        <span className="truncate">{adding ? 'Adding…' : 'Add to Cart'}</span>
      </button>
      <button
        type="button"
        onClick={handleBuyNow}
        className={`${sc('pdpBuyNowBtn')} ${mobileCompact ? 'px-3 py-3 text-xs sm:text-sm' : ''}`}
      >
        <span className="truncate">Buy Now</span>
      </button>
    </div>
  );

  const breadcrumb = [
    { label: 'Home', to: '/' },
    listing.subSystem ? { label: listing.subSystem, to: '/catalog' } : null,
    backHref ? { label: 'All sellers', to: backHref } : null,
    { label: listing.name, to: null },
  ].filter(Boolean);

  const highlightRows = [
    listing.partNumber ? { label: 'Part number', value: listing.partNumber } : null,
    listing.brand && listing.brand !== '—' ? { label: 'Brand', value: listing.brand } : null,
    listing.conditionLabel ? { label: 'Condition', value: listing.conditionLabel } : null,
    listing.isOem != null
      ? { label: 'Origin', value: listing.isOem ? 'OEM' : 'Aftermarket' }
      : null,
    listing.warrantyMonths != null
      ? { label: 'Warranty', value: `${listing.warrantyMonths} months` }
      : null,
    listing.subSystem ? { label: 'Category', value: listing.subSystem } : null,
  ].filter(Boolean);

  const offerLines = [
    hasDiscount
      ? `Special Price — save on MRP for this genuine fitment part`
      : null,
    listing.warrantyMonths
      ? `${listing.warrantyMonths}-month seller warranty included`
      : 'Seller warranty available on eligible parts',
    'Free delivery on orders above ₹1,999',
    '7-day easy return on unused parts (seller terms apply)',
  ].filter(Boolean);

  return (
    <div className={`${sc('pdpShell')} pb-[calc(8.75rem+env(safe-area-inset-bottom,0px))] md:pb-8`}>
      <div className={`border-b ${isDark ? 'border-slate-800/80 bg-[#0b1120]' : 'border-slate-200 bg-white'}`}>
        <div className={`mx-auto max-w-[1200px] px-3 py-2 text-xs sm:px-4 sm:text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          <nav className="flex flex-wrap items-center gap-1">
            {breadcrumb.map((crumb, i) => (
              <span key={crumb.label} className="flex items-center gap-1">
                {i > 0 && <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>/</span>}
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className={`hover:underline ${isDark ? 'text-[#8ab4f8]' : 'text-[#2874f0]'}`}
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={`line-clamp-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-0 py-0 sm:px-3 sm:py-3 md:px-4">
        <div
          className={`overflow-hidden sm:rounded-sm sm:border ${
            isDark
              ? 'sm:border-slate-700/60 sm:bg-slate-900/40'
              : 'bg-white sm:border-slate-200 sm:shadow-sm'
          }`}
        >
          <div className="grid grid-cols-1 gap-0 md:grid-cols-12">
            {/* Gallery column — Flipkart: sticky image + CTAs under it */}
            <div className="md:col-span-5 lg:col-span-5">
              <div className="md:sticky md:top-[72px]">
                <div
                  className={`md:min-h-[520px] md:border-r ${
                    isDark ? 'md:border-slate-700/60' : 'md:border-slate-100'
                  }`}
                >
                  <PhotoGallery
                    photos={listing.photos}
                    onWishlist={handleNotifyMe}
                    onShare={handleShare}
                    footer={
                      showOos ? null : (
                        <div className="w-full">{actionButtons()}</div>
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Buy box */}
            <div className="md:col-span-7 lg:col-span-7">
              <div className="flex min-h-0 flex-col p-4 sm:p-5 md:min-h-[520px] md:p-6 lg:px-8 lg:py-6">
                {listing.brand && listing.brand !== '—' && (
                  <Link
                    to={`/brands?search=${encodeURIComponent(listing.brand)}`}
                    className={`text-sm font-medium hover:underline ${
                      isDark ? 'text-[#8ab4f8]' : 'text-[#2874f0]'
                    }`}
                  >
                    {listing.brand}
                  </Link>
                )}

                <h1
                  className={`mt-1 text-[16px] font-normal leading-snug sm:text-[18px] lg:text-[20px] ${
                    isDark ? 'text-slate-100' : 'text-slate-900'
                  }`}
                >
                  {listing.name}
                  {listing.conditionLabel && (
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                      {' '}
                      ({listing.conditionLabel})
                    </span>
                  )}
                </h1>

                {/* Rating row — Flipkart style */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {sellerRating > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-[#388e3c] px-1.5 py-0.5 text-xs font-semibold text-white">
                      {sellerRating.toFixed(1)}
                      <FaStar className="text-[9px]" />
                    </span>
                  )}
                  {listing.isOem && (
                    <span
                      className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        isDark
                          ? 'bg-blue-950/50 text-blue-300 ring-1 ring-blue-700/50'
                          : 'bg-blue-50 text-[#2874f0] ring-1 ring-blue-200'
                      }`}
                    >
                      OEM
                    </span>
                  )}
                  {hasDiscount && (
                    <span
                      className={`text-xs font-medium ${
                        isDark ? 'text-emerald-400' : 'text-[#388e3c]'
                      }`}
                    >
                      Hot Deal
                    </span>
                  )}
                  {listing.partNumber && (
                    <span className={`font-mono text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                      Part # {listing.partNumber}
                    </span>
                  )}
                </div>

                <div className={`mt-4 border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                  <PriceBreakdownCard pricing={listing.pricing} embedded />
                </div>

                {/* Available offers */}
                <div className={`mt-5 border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                  <h2
                    className={`text-sm font-medium ${
                      isDark ? 'text-slate-200' : 'text-slate-900'
                    }`}
                  >
                    Available offers
                  </h2>
                  <ul className="mt-2 space-y-2">
                    {offerLines.map((line) => (
                      <li
                        key={line}
                        className={`flex gap-2 text-xs leading-snug sm:text-sm ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <span
                          className={`mt-0.5 shrink-0 text-[10px] font-bold uppercase ${
                            isDark ? 'text-emerald-400' : 'text-[#388e3c]'
                          }`}
                        >
                          Offer
                        </span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Delivery / services */}
                <div className={`mt-5 border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                  <h2
                    className={`mb-2 text-sm font-medium ${
                      isDark ? 'text-slate-200' : 'text-slate-900'
                    }`}
                  >
                    Delivery &amp; services
                  </h2>
                  <SellerBlock
                    seller={listing.seller}
                    eta={listing.etaLabel || listing.eta}
                    warrantyMonths={listing.warrantyMonths}
                    embedded
                  />
                </div>

                <div className="mt-4">
                  <p
                    className={`mb-2 text-sm font-medium ${
                      isDark ? 'text-slate-200' : 'text-slate-900'
                    }`}
                  >
                    Vehicle fitment
                  </p>
                  <FitmentStrip fitment={listing.fitment} compact />
                </div>

                {/* Highlights / specs — Flipkart table */}
                {highlightRows.length > 0 && (
                  <div className={`mt-5 border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                    <h2
                      className={`mb-3 text-sm font-medium ${
                        isDark ? 'text-slate-200' : 'text-slate-900'
                      }`}
                    >
                      Highlights
                    </h2>
                    <dl
                      className={`overflow-hidden rounded-sm border text-sm ${
                        isDark ? 'border-slate-700/60' : 'border-slate-200'
                      }`}
                    >
                      {highlightRows.map((row, idx) => (
                        <div
                          key={row.label}
                          className={`grid grid-cols-5 gap-2 px-3 py-2.5 sm:grid-cols-4 ${
                            idx % 2 === 0
                              ? isDark
                                ? 'bg-slate-900/40'
                                : 'bg-slate-50'
                              : isDark
                                ? 'bg-transparent'
                                : 'bg-white'
                          }`}
                        >
                          <dt
                            className={`col-span-2 sm:col-span-1 ${
                              isDark ? 'text-slate-400' : 'text-slate-500'
                            }`}
                          >
                            {row.label}
                          </dt>
                          <dd
                            className={`col-span-3 font-medium sm:col-span-3 ${
                              isDark ? 'text-slate-100' : 'text-slate-900'
                            }`}
                          >
                            {row.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {listing.description && (
                  <div className={`mt-5 border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                    <h2
                      className={`text-sm font-medium ${
                        isDark ? 'text-slate-200' : 'text-slate-900'
                      }`}
                    >
                      Product details
                    </h2>
                    <p
                      className={`mt-2 text-sm leading-relaxed ${
                        isDark ? 'text-slate-400' : 'text-slate-600'
                      }`}
                    >
                      {listing.description}
                    </p>
                  </div>
                )}

                {showOos && (
                  <div
                    className={`mt-6 rounded-sm border p-4 text-center ${
                      isDark
                        ? 'border-red-800/50 bg-red-950/30'
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <p
                      className={`font-semibold ${
                        isDark ? 'text-red-200' : 'text-red-700'
                      }`}
                    >
                      This item just went out of stock.
                    </p>
                    <button
                      type="button"
                      className={`mt-3 inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-sm font-semibold ${
                        isDark
                          ? 'border-red-700 text-red-200 hover:bg-red-900/40'
                          : 'border-red-300 text-red-700 hover:bg-red-100'
                      }`}
                      onClick={handleNotifyMe}
                    >
                      <FaBell /> Notify me
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-3 px-3 sm:px-0">
          <MoreSellersSection
            listings={moreSellers}
            currentListingId={listing.listingId}
            nodeId={nodeId}
            column={columnParam}
            vehicleId={resolvedVehicleId}
            loading={sellersLoading}
          />

          <QASection
            threads={qaThreads}
            onAsk={handleAskQuestion}
            isAuthenticated={isAuthenticated}
            onLoginRequired={handleLoginForQa}
          />

          <CrossSellSection items={crossSell} />
        </div>
      </div>

      {!showOos && (
        <div
          className={`fixed inset-x-0 z-[110] border-t px-3 py-2.5 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur-md md:hidden bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] ${
            isDark ? 'border-slate-700/80 bg-[#0b1120]/95' : 'border-slate-200 bg-white/95'
          }`}
        >
          {actionButtons('', true)}
        </div>
      )}
    </div>
  );
}
